import { useMutation, useQuery } from "@apollo/client";
import { AppPageNoTopPadding } from "layouts";
import { useParams } from "react-router-dom";
import { GET_SINGLE_USER, SEND_ACCESS_NOTIFICATION } from "./queries";
import { PlaylistsComponent } from "./components/Playlists";
import { UserInfoComponent } from "./components/UserInfo";
import { UsageHistory } from "./components/UsageHistory";
import { CallSchedules } from "./components/CallSchedules";
import {
  Tabs,
  Tab,
  Typography,
  Box,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import { useEffect, useState } from "react";
import { AccessNotificationInput, Response, TabPanelProps } from "./types";
import { Loader } from "./styles";
import { TreatmentHistoryResponse } from "./components/UserInfo/components/TreatmentHistoryTable/types";
import { GET_TREATMENT_HISTORY } from "./components/UserInfo/components/TreatmentHistoryTable/queries";

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          <div>{children}</div>
          {/* <Typography>{children}</Typography> */}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const savedTabFunc = () => {
  const saved = localStorage.getItem("tabValue");
  const savedTab = JSON.parse(saved || "");
  return savedTab;
};

export const UserDetails = () => {
  const { id } = useParams();
  const userId: number = parseInt(id ?? "") || 0;
  const [tabValue, setTabValue] = useState<number>(savedTabFunc() || 0);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showError, setShowError] = useState<boolean>(false);

  const handleChange = (_event, newValue: number) => {
    setTabValue(newValue);
  };

  const [sendAccessNotification] = useMutation<AccessNotificationInput>(
    SEND_ACCESS_NOTIFICATION,
    {
      variables: {
        accountId: userId,
        accessedBy: localStorage.getItem("username"),
        source: "admin panel",
      },
    },
  );
  useEffect(() => {
    sendAccessNotification();
  }, []);

  const { data, error, loading, refetch } = useQuery<Response>(
    GET_SINGLE_USER,
    {
      variables: { userId },
      fetchPolicy: "network-only",
    },
  );

  const account = data?.account;

  const {
    data: TreatmentData,
    loading: TreatmentLoading,
    refetch: AllProgramsRefetch,
  } = useQuery<TreatmentHistoryResponse>(GET_TREATMENT_HISTORY, {
    variables: { accountId: account?.id },
    fetchPolicy: "network-only",
    skip: !account?.id,
  });

  useEffect(() => {
    setIsLoading(loading || TreatmentLoading);
  }, [loading, TreatmentLoading]);

  useEffect(() => {
    if (error) {
      setShowError(true);
    }
  }, [error]);

  useEffect(() => {
    localStorage.setItem("tabValue", JSON.stringify(tabValue));
  }, [tabValue]);

  useEffect(() => {
    if (
      TreatmentData?.treatmentHistory &&
      TreatmentData.treatmentHistory.length > 0
    ) {
      refetch();
    }
  }, [TreatmentData?.treatmentHistory?.length, refetch]);

  const inMaintenance =
    account?.latest_program_type?.toLowerCase() === "maintenance";

  return (
    <AppPageNoTopPadding>
      {isLoading || !account ? (
        <Loader>
          <CircularProgress />
        </Loader>
      ) : (
        <Box sx={{ width: "100%" }}>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Box
              sx={{
                margin: "1rem 1rem 0 1rem",
                width: "max-content",
                borderBottom: 1,
                borderColor: "divider",
              }}
            >
              <Typography variant="h6">
                {account?.firstName} {account?.lastName}
              </Typography>
            </Box>
            <Tabs
              value={tabValue}
              onChange={handleChange}
              aria-label="basic tabs example"
            >
              <Tab label="Patient Info" {...a11yProps(0)} />
              <Tab label="Playlists" {...a11yProps(1)} />
              <Tab label="Usage History" {...a11yProps(2)} />
              <Tab label="Call Schedules" {...a11yProps(3)} />
            </Tabs>
          </Box>
          <TabPanel value={tabValue} index={0}>
            <UserInfoComponent
              account={account}
              AllProgramsData={TreatmentData}
              ProgramsReftech={AllProgramsRefetch}
            />
          </TabPanel>
          <TabPanel value={tabValue} index={1}>
            <PlaylistsComponent
              account={account}
              AllProgramsData={TreatmentData}
              ProgramsReftech={AllProgramsRefetch}
              inMaintenance={inMaintenance}
            />
          </TabPanel>
          <TabPanel value={tabValue} index={2}>
            <UsageHistory account={account} accountId={userId} />
          </TabPanel>
          <TabPanel value={tabValue} index={3}>
            <CallSchedules
              callSchedules={account?.callSchedules ?? []}
              userId={userId}
              playlists={account?.playlists ?? []}
              inMaintenance={inMaintenance}
            />
          </TabPanel>
        </Box>
      )}
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        open={showError}
        onClose={() => setShowError(false)}
      >
        <Alert onClose={() => setShowError(false)} severity="error">
          Failed to fetch single patient data.
        </Alert>
      </Snackbar>
    </AppPageNoTopPadding>
  );
};
